import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, LinearProgress, Chip,
} from '@mui/material';
import { Search as SearchIcon, Add, ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { eventBus } from '../../services/EventBus';
import {
  sonosDiscover, sonosAddByIp, fetchSonosAdminSummary, fetchSonosSpeakers,
  SonosSpeaker,
} from '../../services/sonosApi';
import {
  DiscoveryCell,
  DiscoveryRegisteredTable,
  discoveryRegisteredShellSx,
  DiscoverySortableHeader,
  discoveryRegisteredCol as col,
  discoveryRegisteredRowSx,
  useDiscoveryColumnSort,
} from '../shared/DiscoverySortableHeader';

const CARD_BG = '#1a1a1a';
const BLUE = '#2391FF';

interface DiscoverResultRow {
  ip: string;
  nombre?: string;
  modelo?: string | null;
  result: 'added' | 'updated_ip' | 'already_known' | 'error';
  detail?: string;
}

type SonosResultSortKey = 'name' | 'ip' | 'model' | 'result';
type SonosDeviceSortKey = 'name' | 'type' | 'ip' | 'model' | 'power' | 'status' | 'via';

interface SonosDiscoveryTabProps {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  onGoToDevices?: () => void;
}

const SonosDiscoveryTab: React.FC<SonosDiscoveryTabProps> = ({
  isDemoUser = false, onDemoActionAttempt, onGoToDevices,
}) => {
  const { t, i18n } = useTranslation();
  const [ipInput, setIpInput] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [adding, setAdding] = useState(false);
  const [progress, setProgress] = useState<{ round: number; total: number } | null>(null);
  const [rows, setRows] = useState<DiscoverResultRow[]>([]);
  const [summary, setSummary] = useState('');
  const [devices, setDevices] = useState<SonosSpeaker[]>([]);
  const [unassigned, setUnassigned] = useState<number | null>(null);
  const [lastRun, setLastRun] = useState<string | null>(localStorage.getItem('lastSonosDiscovery'));
  const [error, setError] = useState<string | null>(null);

  const refreshDevices = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([
        fetchSonosAdminSummary(),
        fetchSonosSpeakers(),
      ]);
      setUnassigned(s.unassigned_count);
      setDevices(list);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { void refreshDevices(); }, [refreshDevices]);

  const markRun = () => {
    const now = new Date();
    const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';
    const formatted = now.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    setLastRun(formatted);
    localStorage.setItem('lastSonosDiscovery', formatted);
  };

  const setCountsSummary = (rowsBuilt: DiscoverResultRow[]) => {
    const added = rowsBuilt.filter((r) => r.result === 'added').length;
    const updated = rowsBuilt.filter((r) => r.result === 'updated_ip').length;
    const known = rowsBuilt.filter((r) => r.result === 'already_known').length;
    setSummary(t('settings.sonosDiscovery.summary', { added, updated, known }));
  };

  const handleDiscover = async () => {
    if (isDemoUser) { onDemoActionAttempt?.(); return; }
    setDiscovering(true);
    setError(null);
    setRows([]);
    setSummary('');
    const rounds = 3;
    setProgress({ round: 1, total: rounds });
    const timer = setInterval(() => {
      setProgress((p) => (p && p.round < p.total ? { ...p, round: p.round + 1 } : p));
    }, 4000);
    try {
      const data = await sonosDiscover();
      clearInterval(timer);
      setProgress(null);
      const built: DiscoverResultRow[] = [
        ...(data.added || []).map((e: any) => ({ ip: e.ip, nombre: e.nombre, modelo: e.modelo, result: 'added' as const })),
        ...(data.updated_ip || []).map((e: any) => ({ ip: e.ip, nombre: e.nombre, modelo: e.modelo, result: 'updated_ip' as const })),
        ...(data.already_known || []).map((e: any) => ({ ip: e.ip, nombre: e.nombre, modelo: e.modelo, result: 'already_known' as const })),
        ...(data.errors || []).map((e: any) => ({ ip: e.ip || '—', nombre: e.uid, result: 'error' as const, detail: e.error })),
      ];
      setRows(built);
      setCountsSummary(built);
      markRun();
      eventBus.emit('estructura:refrescar');
      void refreshDevices();
    } catch (e: any) {
      clearInterval(timer);
      setProgress(null);
      setError(e?.response?.data?.detail || t('settings.sonosDiscovery.errorDiscover'));
    } finally {
      setDiscovering(false);
    }
  };

  const handleAddByIp = async () => {
    if (isDemoUser) { onDemoActionAttempt?.(); return; }
    const ips = ipInput.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (ips.length === 0) return;
    setAdding(true);
    setError(null);
    try {
      const data = await sonosAddByIp(ips);
      const built: DiscoverResultRow[] = (data.results || []).map((r: any) => ({
        ip: r.ip,
        nombre: r.speaker?.nombre,
        modelo: r.speaker?.modelo,
        result: r.result,
        detail: r.error,
      }));
      setRows(built);
      setCountsSummary(built);
      setIpInput('');
      markRun();
      eventBus.emit('estructura:refrescar');
      void refreshDevices();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('settings.sonosDiscovery.errorAdd'));
    } finally {
      setAdding(false);
    }
  };

  const resultLabel = (r: DiscoverResultRow) => {
    switch (r.result) {
      case 'added': return { text: `✓ ${t('settings.sonosDiscovery.resultAdded')}`, color: '#66bb6a' };
      case 'updated_ip': return { text: `↻ ${t('settings.sonosDiscovery.resultUpdated')}`, color: '#e5a54b' };
      case 'already_known': return { text: `— ${t('settings.sonosDiscovery.resultKnown')}`, color: '#999' };
      default: return { text: `✕ ${r.detail || t('settings.sonosDiscovery.resultError')}`, color: '#e57373' };
    }
  };

  const getResultSortValue = useCallback((r: DiscoverResultRow, key: SonosResultSortKey) => {
    if (key === 'name') return r.nombre || r.ip || '';
    if (key === 'ip') return r.ip || '';
    if (key === 'model') return r.modelo || '';
    return resultLabel(r).text;
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- labels follow t

  const getDeviceSortValue = useCallback((d: SonosSpeaker, key: SonosDeviceSortKey) => {
    if (key === 'name') return d.nombre || d.ip || '';
    if (key === 'type') return t('settings.discoveryCols.kindSpeaker');
    if (key === 'ip') return d.ip || '';
    if (key === 'model') return d.modelo || '';
    if (key === 'power') return '';
    if (key === 'via') return t('settings.discoveryCols.viaLan');
    return d.online
      ? t('settings.discoveryCols.statusOnline')
      : t('settings.discoveryCols.statusOffline');
  }, [t]);

  const {
    sorted: sortedRows, sortKey: resultSortKey, sortDir: resultSortDir, toggleSort: toggleResultSort,
  } = useDiscoveryColumnSort(rows, getResultSortValue);
  const {
    sorted: sortedDevices, sortKey: deviceSortKey, sortDir: deviceSortDir, toggleSort: toggleDeviceSort,
  } = useDiscoveryColumnSort(devices, getDeviceSortValue);

  const deviceRowSx = {
    display: 'flex',
    alignItems: 'center',
    py: 0.45,
    borderBottom: '1px solid #262626',
    minWidth: 0,
  } as const;

  const colNameSx = {
    width: '35%',
    flex: '0 0 35%',
    minWidth: 0,
    fontSize: '0.78rem',
    color: '#fff',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    pr: 0.5,
  } as const;

  const colIpSx = {
    width: '20%',
    flex: '0 0 20%',
    minWidth: 0,
    fontSize: '0.7rem',
    color: '#888',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    pr: 0.5,
  } as const;

  const colModelSx = {
    width: '23%',
    flex: '0 0 23%',
    minWidth: 0,
    fontSize: '0.7rem',
    color: '#888',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    pr: 0.5,
  } as const;

  const colStatusSx = {
    width: '22%',
    flex: '0 0 22%',
    minWidth: 0,
    fontSize: '0.7rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <Box sx={{ color: 'white', px: 0.5, pb: 2, touchAction: 'pan-y' }}>
      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 1.25, lineHeight: 1.4 }}>
        {t('settings.sonosDiscovery.help')}
      </Typography>

      {/* Descubrimiento en red */}
      <Box sx={{ backgroundColor: CARD_BG, borderRadius: 1, p: 1.5, mb: 1.5, border: '1px solid #333' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {t('settings.sonosDiscovery.discoverTitle')}
          </Typography>
          {lastRun && (
            <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>
              {t('settings.sonosDiscovery.lastRun')}: {lastRun}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<SearchIcon />}
          disabled={discovering}
          onClick={handleDiscover}
          sx={{ backgroundColor: BLUE, textTransform: 'none', '&:hover': { backgroundColor: '#1a7ae0' } }}
        >
          {discovering ? t('settings.sonosDiscovery.discovering') : t('settings.sonosDiscovery.discoverBtn')}
        </Button>
        {progress && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#aaa', mb: 0.5 }}>
              {t('settings.sonosDiscovery.round', { round: progress.round, total: progress.total })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progress.round / progress.total) * 100}
              sx={{ height: 6, borderRadius: 3, backgroundColor: '#333', '& .MuiLinearProgress-bar': { backgroundColor: BLUE } }}
            />
          </Box>
        )}
      </Box>

      {/* Alta por IP */}
      <Box sx={{ backgroundColor: CARD_BG, borderRadius: 1, p: 1.5, mb: 1.5, border: '1px solid #333' }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
          {t('settings.sonosDiscovery.addByIpTitle')}
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={1}
          maxRows={5}
          placeholder={t('settings.sonosDiscovery.ipPlaceholder')}
          value={ipInput}
          onChange={(e) => setIpInput(e.target.value)}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': { backgroundColor: '#0f0f0f', color: '#fff' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
          }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          disabled={adding || !ipInput.trim()}
          onClick={handleAddByIp}
          sx={{ backgroundColor: BLUE, textTransform: 'none', '&:hover': { backgroundColor: '#1a7ae0' } }}
        >
          {t('settings.sonosDiscovery.addBtn')}
        </Button>
      </Box>

      {error && <Typography sx={{ color: '#e57373', fontSize: '0.8rem', mb: 1 }}>{error}</Typography>}

      {/* Resultado del último descubrimiento */}
      {(rows.length > 0 || summary) && (
        <Box sx={{ backgroundColor: CARD_BG, borderRadius: 1, p: 1.5, mb: 1.5, border: '1px solid #333' }}>
          {summary && <Typography sx={{ fontSize: '0.8rem', color: BLUE, mb: 1 }}>{summary}</Typography>}
          {rows.length > 0 && (
            <>
              <Typography sx={{ fontSize: '0.72rem', color: '#888', mb: 0.5 }}>
                {t('settings.sonosDiscovery.lastRunNote')}
              </Typography>
              <DiscoverySortableHeader
                columns={[
                  { key: 'name', label: t('settings.discoveryCols.name', { defaultValue: 'Nombre' }), sx: colNameSx },
                  { key: 'ip', label: t('settings.discoveryCols.ip', { defaultValue: 'IP' }), sx: colIpSx },
                  { key: 'model', label: t('settings.discoveryCols.model', { defaultValue: 'Modelo' }), sx: colModelSx },
                  { key: 'result', label: t('settings.discoveryCols.result', { defaultValue: 'Resultado' }), sx: colStatusSx },
                ]}
                sortKey={resultSortKey}
                sortDir={resultSortDir}
                onToggle={toggleResultSort}
              />
              {sortedRows.map((r, i) => {
                const rl = resultLabel(r);
                return (
                  <Box key={`${r.ip}-${i}`} sx={deviceRowSx}>
                    <Typography sx={colNameSx} title={r.nombre || r.ip}>{r.nombre || r.ip}</Typography>
                    <Typography sx={colIpSx}>{r.ip}</Typography>
                    <Typography sx={colModelSx} title={r.modelo || undefined}>{r.modelo || '—'}</Typography>
                    <Typography sx={{ ...colStatusSx, color: rl.color }}>{rl.text}</Typography>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      )}

      {/* Dispositivos registrados (DB) */}
      <Box sx={discoveryRegisteredShellSx()}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
          {t('settings.sonosDiscovery.devicesTitle')}
        </Typography>
        {devices.length === 0 ? (
          <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>
            {t('settings.sonosDiscovery.emptyDevices')}
          </Typography>
        ) : (
          <>
            <DiscoveryRegisteredTable
              columns={[
                { key: 'name', label: t('settings.discoveryCols.name'), sx: col.name },
                { key: 'type', label: t('settings.discoveryCols.type'), sx: col.type },
                { key: 'ip', label: t('settings.discoveryCols.ip'), sx: col.ip },
                { key: 'model', label: t('settings.discoveryCols.model'), sx: col.model },
                { key: 'power', label: t('settings.discoveryCols.power'), sx: col.power },
                { key: 'status', label: t('settings.discoveryCols.status'), sx: col.status },
                { key: 'via', label: t('settings.discoveryCols.via'), sx: col.via },
              ]}
              sortKey={deviceSortKey}
              sortDir={deviceSortDir}
              onToggle={toggleDeviceSort}
            >
            {sortedDevices.map((d) => (
              <Box key={d.id} sx={discoveryRegisteredRowSx}>
                <DiscoveryCell label={t('settings.discoveryCols.name')} sx={col.name} title={d.nombre || d.ip}>{d.nombre || d.ip}</DiscoveryCell>
                <DiscoveryCell label={t('settings.discoveryCols.type')} sx={col.type}>{t('settings.discoveryCols.kindSpeaker')}</DiscoveryCell>
                <DiscoveryCell label={t('settings.discoveryCols.ip')} sx={col.ip}>{d.ip || '—'}</DiscoveryCell>
                <DiscoveryCell label={t('settings.discoveryCols.model')} sx={col.model} title={d.modelo || undefined}>{d.modelo || '—'}</DiscoveryCell>
                <DiscoveryCell label={t('settings.discoveryCols.power')} sx={col.power}>—</DiscoveryCell>
                <DiscoveryCell
                  label={t('settings.discoveryCols.status')}
                  sx={{ ...col.status, color: d.online ? '#66bb6a' : '#999' }}
                >
                  {d.online
                    ? t('settings.discoveryCols.statusOnline')
                    : t('settings.discoveryCols.statusOffline')}
                </DiscoveryCell>
                <DiscoveryCell label={t('settings.discoveryCols.via')} sx={col.via}>{t('settings.discoveryCols.viaLan')}</DiscoveryCell>
              </Box>
            ))}
            </DiscoveryRegisteredTable>
          </>
        )}
      </Box>

      {unassigned !== null && unassigned > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            icon={<ArrowForward />}
            label={t('settings.sonosDiscovery.goToDevices', { count: unassigned })}
            onClick={onGoToDevices}
            clickable={!!onGoToDevices}
            sx={{ backgroundColor: '#262626', color: '#fff', '& .MuiChip-icon': { color: BLUE } }}
          />
        </Box>
      )}
    </Box>
  );
};

export default SonosDiscoveryTab;
